/*
  Proposals: the reviewer's and the agent's way through, made reachable.

  The server half has been complete since P3 — create, list, get, apply, close, with the commands validated
  at the door AND again on apply, and an applied proposal running through `CommandService` as one batch
  exactly as if the applying editor had typed it. P5ao's realtime test proves an apply publishes to the
  room. And `grep -rln proposal apps/web/src` returned nothing: the entire mechanism was reachable only by
  `curl`, which is the same shape as the membership surface P5ao found and, before that, as the conflict
  notice that shipped dead.

  P5ap opened notes to reviewers and recorded, explicitly, that "raising a batch of commands for an editor
  to review as a diff remains P6". This is that.

  WHAT A DIFF HONESTLY IS HERE. Not a prediction of the resulting design: `ProposalService` states that the
  commands are "NOT re-resolved against current state", so a proposal raised this morning may not apply this
  afternoon, and a modal that drew the after-state would be drawing a guess. What it shows is what was
  ASKED FOR — every command, in order, with ids resolved to names — which is the thing the applying editor
  is taking responsibility for. `describeCommand` in core does the rendering and says more about why.
*/
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal.js';
import { describeBatch, type SeparationState } from '@integrate/core';
import type { ProposalDetail, ProposalSummary } from '@integrate/contracts';
import { api, ApiError } from '../api.js';
import { Loading } from '../ui/States.js';

const STATUS_LABEL: Record<ProposalSummary['status'], string> = {
  open: 'Open',
  applied: 'Applied',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

export function ProposalsModal({
  projectId,
  state,
  canApply,
  myUserId,
  onClose,
}: {
  projectId: string;
  /** For resolving employee ids to the names the reader knows them by. */
  state: SeparationState | null;
  /** Applying is a write, so it is `canWrite` — a reviewer raises them and cannot accept them. */
  canApply: boolean;
  myUserId: string | null;
  onClose(): void;
}): JSX.Element {
  const [list, setList] = useState<ProposalSummary[] | null>(null);
  const [open, setOpen] = useState<ProposalDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = async (): Promise<void> => {
    try {
      const { proposals } = await api.proposals(projectId);
      setList(proposals);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the proposals.');
    }
  };

  useEffect(() => {
    void reload();
  }, [projectId]);

  /** Every write goes through here, so the server's refusal has one place to land — verbatim. */
  const run = async (action: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const nameOf = (id: string): string | undefined =>
    state?.employees.find((p) => p.id === id)?.name ??
    state?.design.buildUps.find((b) => b.id === id)?.name;

  const expand = async (id: string): Promise<void> => {
    if (open?.id === id) {
      setOpen(null);
      return;
    }
    try {
      const { proposal } = await api.proposal(projectId, id);
      setOpen(proposal);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load that proposal.');
    }
  };

  return (
    <Modal
      testId="proposals-modal"
      title="Proposals"
      onClose={onClose}
    >
      <p className="muted" style={{ marginTop: 0 }}>
        A change somebody suggested but may not make. Applying one runs its commands as if you had typed
        them — one batch, one entry in the history, undoable like any other change. The events will say
        you made them, because you did; who suggested it stays on the proposal.
      </p>

      {error && (
        <p className="bad" data-testid="proposals-error" role="alert">
          {error}
        </p>
      )}

      {list === null ? (
        <Loading what="the proposals" testId="proposals-loading" />
      ) : list.length === 0 ? (
        <p className="muted" data-testid="proposals-empty">
          Nothing has been proposed on this project yet.
        </p>
      ) : (
        <ul className="hlist" data-testid="proposals-list">
          {list.map((p) => (
            <li key={p.id} data-testid={`proposal-${p.id}`}>
              <div className="hrow">
                <button
                  className="linkish"
                  data-testid={`proposal-open-${p.id}`}
                  onClick={() => void expand(p.id)}
                >
                  {open?.id === p.id ? '▾ ' : '▸ '}
                  {p.title}
                </button>
                <span className="muted small">
                  {' · '}
                  {p.commandCount} {p.commandCount === 1 ? 'change' : 'changes'}
                  {' · '}
                  {/*
                    The name, not the id. `list` and `get` returned `createdBy` alone until this
                    milestone, so a proposals list could say "proposed by u_3f8a…" — the same defect
                    P5ak fixed three times in the history modal, in the one place where naming the
                    person is the point rather than a courtesy.
                  */}
                  {p.createdByName ?? 'somebody who has since left'}
                  {p.actorKind === 'agent' && (
                    <span className="pill" title="Suggested by the assistant, not by a person">
                      assistant
                    </span>
                  )}
                  {' · '}
                  <span data-testid={`proposal-status-${p.id}`}>{STATUS_LABEL[p.status]}</span>
                </span>
              </div>

              {open?.id === p.id && (
                <div data-testid={`proposal-diff-${p.id}`}>
                  {open.transcript && (
                    <p className="muted small" data-testid={`proposal-transcript-${p.id}`}>
                      “{open.transcript}”
                    </p>
                  )}
                  <table className="tsa-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Change</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/*
                        `describeBatch`, not one command at a time: a planned hire's bucket lives in a
                        SIBLING command, so the row that says which side they land on can only be
                        written by something that has read the whole proposal.
                      */}
                      {describeBatch(open.commands, nameOf).map((described, i) => {
                        return (
                          <tr key={i} data-testid={`proposal-command-${i}`}>
                            <td className="muted num">{i + 1}</td>
                            <td>{described.label}</td>
                            <td className="muted small">
                              {described.fields
                                .map((f) => `${f.key}: ${f.value}`)
                                .join(' · ')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {p.status === 'open' && (
                    <div className="hact">
                      {canApply && (
                        <button
                          className="primary"
                          data-testid={`proposal-apply-${p.id}`}
                          disabled={busy}
                          onClick={() => void run(() => api.applyProposal(projectId, p.id))}
                        >
                          Apply these {p.commandCount}
                        </button>
                      )}
                      {canApply && (
                        <button
                          data-testid={`proposal-reject-${p.id}`}
                          disabled={busy}
                          onClick={() =>
                            void run(() => api.closeProposal(projectId, p.id, 'rejected'))
                          }
                        >
                          Reject
                        </button>
                      )}
                      {/*
                        Withdrawing is the PROPOSER's word for the same act, and it is offered to them
                        whether or not they may write — taking back your own suggestion is not an edit
                        to the project. The server decides which of the two verbs a given reader may
                        issue; this only decides which button to draw.
                      */}
                      {p.createdBy === myUserId && (
                        <button
                          data-testid={`proposal-withdraw-${p.id}`}
                          disabled={busy}
                          onClick={() =>
                            void run(() => api.closeProposal(projectId, p.id, 'withdrawn'))
                          }
                        >
                          Withdraw
                        </button>
                      )}
                      {!canApply && p.createdBy !== myUserId && (
                        <span className="muted small">
                          Waiting for somebody who can edit this project.
                        </span>
                      )}
                    </div>
                  )}
                  {p.status !== 'open' && p.reviewedByName && (
                    <p className="muted small" data-testid={`proposal-reviewed-${p.id}`}>
                      {STATUS_LABEL[p.status]} by {p.reviewedByName}
                      {p.reviewNote ? ` — ${p.reviewNote}` : ''}
                    </p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
